/**
 * TransactionsTab — every transaction visible to the caller's scope.
 * Operator sees their location's full feed; cashier sees only their
 * sub-group. Defaults to today; supports a date range, bucket filter
 * (in/out/casino/sports), and a free-text player filter.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { PlayerName } from './PlayerDrilldownContext';
import {
    Loader2, RefreshCw, Search, Download, ArrowUpRight, ArrowDownRight,
    Dice5, Trophy, Coins, Radio,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtTime = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleString(); } catch { return iso; } };

const today = () => new Date().toISOString().slice(0, 10);

const TXN_LABEL = {
    cashier_load: 'Counter load', cashier_cashout: 'Counter cashout',
    cashier_freeplay: 'Free play', cashier_edit_player: 'Customer edit',
    kiosk_cash: 'Kiosk cash-in', cashout_request: 'Cashout request',
    cashout: 'Cashout', cashout_play_back: 'Play-it-back',
    peer_transfer_in: 'Peer in', peer_transfer_out: 'Peer out',
    admin_credit: 'Admin credit', admin_deposit: 'Admin deposit',
    admin_withdraw: 'Admin withdraw', credit_load: 'Credit load',
    credit_cashout: 'Credit cashout', daily_bonus: 'Daily bonus',
    birthday_bonus: 'Birthday bonus', bonus: 'Bonus',
    referral_bonus: 'Referral bonus', cashout_refund: 'Cashout refund',
    slot_spin: 'Slot spin', slot_wager: 'Slot wager', slot_win: 'Slot win',
    jackpot_grand: 'GRAND jackpot', jackpot_super: 'SUPER jackpot',
    lucky_pack_entry: 'Lucky pack entry', lucky_pack_payout: 'Lucky pack payout',
    series_pack_entry: 'Series pack entry', series_pack_payout: 'Series pack payout',
    majik_topup: 'Majik top-up', majik_redeem: 'Majik redeem',
    sports_bet: 'Sports bet',
};
const labelFor = (tt) => TXN_LABEL[tt] || (tt || '').replace(/_/g, ' ');

const BUCKETS = [
    { value: 'all', label: 'All' },
    { value: 'in', label: 'Ins' },
    { value: 'out', label: 'Outs' },
    { value: 'casino', label: 'Slots' },
    { value: 'sports', label: 'Sports' },
    { value: 'other', label: 'Other' },
];

const TransactionsTab = ({ endpoint = '/cashier/transactions' }) => {
    const [from, setFrom] = useState(today());
    const [to, setTo] = useState(today());
    const [bucket, setBucket] = useState('all');
    const [q, setQ] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [livePulse, setLivePulse] = useState(false);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [lastSeenIds, setLastSeenIds] = useState(new Set());
    const pollTimer = useRef(null);

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const params = { date_from: from, date_to: to, limit: 500 };
            if (bucket !== 'all') params.bucket = bucket;
            if (q.trim()) params.q = q.trim();
            const { data: resp } = await axios.get(`${API}${endpoint}`, { params, withCredentials: true });
            // Pulse the indicator if NEW transactions arrived since last poll
            if (silent && resp?.transactions?.length) {
                const fresh = resp.transactions.filter(t => !lastSeenIds.has(t.id));
                if (fresh.length > 0) {
                    setLivePulse(true);
                    setTimeout(() => setLivePulse(false), 1200);
                }
            }
            setLastSeenIds(new Set((resp?.transactions || []).map(t => t.id)));
            setData(resp);
        } catch (err) { console.error(err); }
        finally { if (!silent) setLoading(false); }
    }, [endpoint, from, to, bucket, q, lastSeenIds]);

    const [pollTick, setPollTick] = useState(0);

    useEffect(() => { load(false); }, [endpoint, from, to, bucket, q]);

    // Silent re-fetch whenever the polling tick increments
    useEffect(() => {
        if (pollTick === 0) return;
        load(true);
    }, [pollTick]);

    // Live polling: bump the tick every 5 s while autoRefresh is on AND viewing today.
    useEffect(() => {
        if (pollTimer.current) clearInterval(pollTimer.current);
        const isToday = (to === today());
        if (!autoRefresh || !isToday) return;
        pollTimer.current = setInterval(() => { setPollTick(n => n + 1); }, 5000);
        return () => clearInterval(pollTimer.current);
    }, [autoRefresh, to]);

    const exportCsv = () => {
        if (!data?.transactions) return;
        const rows = [
            ['When', 'Player', 'Phone', 'Type', 'Bucket', 'Amount', 'Balance after', 'Cashier', 'Status', 'Method', 'Description'],
            ...data.transactions.map(t => [
                fmtTime(t.created_at), t.player_name || '', t.player_phone || '',
                labelFor(t.type), t.bucket || '', (Number(t.amount) || 0).toFixed(2),
                (Number(t.balance_after) || 0).toFixed(2), t.cashier_name || '',
                t.status || '', t.payout_method || '', t.description || t.note || '',
            ]),
        ];
        const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `transactions_${from}_to_${to}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const totals = (data?.transactions || []).reduce((acc, t) => {
        const amt = Math.abs(Number(t.amount) || 0);
        if (t.bucket === 'in') acc.in += amt;
        else if (t.bucket === 'out') acc.out += amt;
        else if (t.bucket === 'casino') {
            if (['slot_win', 'jackpot_grand', 'jackpot_super', 'lucky_pack_payout', 'series_pack_payout'].includes(t.type)) acc.casinoWon += amt;
            else acc.casinoWagered += amt;
        } else if (t.bucket === 'sports') acc.sportsBet += amt;
        return acc;
    }, { in: 0, out: 0, casinoWagered: 0, casinoWon: 0, sportsBet: 0 });

    return (
        <div className="space-y-4" data-testid="transactions-tab">
            <div className="rounded-md border border-zinc-800 bg-zinc-900/30 p-4">
                <div className="flex flex-wrap items-end gap-3">
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-medium mb-1">From</p>
                        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                            className="bg-zinc-950 border-zinc-800 text-zinc-100 h-9 w-40" data-testid="txn-from" />
                    </div>
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-medium mb-1">To</p>
                        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                            className="bg-zinc-950 border-zinc-800 text-zinc-100 h-9 w-40" data-testid="txn-to" />
                    </div>
                    <div className="flex-1 min-w-[200px]">
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-medium mb-1">Find player</p>
                        <div className="relative">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                            <Input value={q} onChange={(e) => setQ(e.target.value)}
                                placeholder="Name or phone…"
                                className="pl-8 bg-zinc-950 border-zinc-800 text-zinc-100 h-9"
                                data-testid="txn-q" />
                        </div>
                    </div>
                    <Button onClick={() => load(false)} size="sm" variant="outline"
                        className="h-9 border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"
                        data-testid="txn-refresh">
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                    <div className="flex items-center gap-1.5 px-2 h-9 rounded-md border border-zinc-800 bg-zinc-950"
                        title={autoRefresh ? 'Live feed on — polls every 5 s' : 'Live feed paused'}
                        data-testid="txn-live-toggle-wrap">
                        <Radio className={`w-3.5 h-3.5 ${autoRefresh ? (livePulse ? 'text-emerald-300' : 'text-emerald-500') : 'text-zinc-600'} ${livePulse ? 'animate-pulse' : ''}`} />
                        <span className={`text-[10px] uppercase tracking-widest font-medium ${autoRefresh ? 'text-emerald-300' : 'text-zinc-500'}`}>
                            {autoRefresh ? 'Live' : 'Paused'}
                        </span>
                        <Switch checked={autoRefresh} onCheckedChange={setAutoRefresh}
                            className="scale-75"
                            data-testid="txn-live-toggle" />
                    </div>
                    <Button onClick={exportCsv} size="sm" variant="outline" disabled={!data?.transactions?.length}
                        className="h-9 border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"
                        data-testid="txn-export">
                        <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
                    </Button>
                </div>

                <div className="mt-3 flex items-center gap-3 flex-wrap">
                    <Tabs value={bucket} onValueChange={setBucket}>
                        <TabsList className="bg-zinc-950 border border-zinc-800 h-8">
                            {BUCKETS.map(b => (
                                <TabsTrigger key={b.value} value={b.value}
                                    className="text-xs data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-100"
                                    data-testid={`txn-bucket-${b.value}`}>
                                    {b.label}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </Tabs>
                </div>
            </div>

            {/* Roll-up bar */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Tile label="Ins" value={fmt(totals.in)} valueClass="text-emerald-300" />
                <Tile label="Outs" value={fmt(totals.out)} valueClass="text-rose-300" />
                <Tile label="Net house" value={fmt(totals.in - totals.out)}
                    valueClass={(totals.in - totals.out) >= 0 ? 'text-emerald-300' : 'text-rose-300'} />
                <Tile label="Slots wagered → won" value={`${fmt(totals.casinoWagered)} → ${fmt(totals.casinoWon)}`} />
                <Tile label="Sports staked" value={fmt(totals.sportsBet)} />
            </div>

            <div className="rounded-md border border-zinc-800 bg-zinc-900/30 overflow-hidden" data-testid="txn-list">
                <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
                    <p className="text-xs text-zinc-500">
                        {loading ? 'Loading…' : `${data?.count || 0} transactions`}
                    </p>
                </div>
                {loading ? (
                    <div className="text-center py-10 text-zinc-500">
                        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                    </div>
                ) : (data?.transactions || []).length === 0 ? (
                    <p className="text-center text-zinc-500 py-10 text-sm">No transactions in this range.</p>
                ) : (
                    <div className="divide-y divide-zinc-800 max-h-[60vh] overflow-y-auto">
                        {data.transactions.map((t) => <Row key={t.id} t={t} />)}
                    </div>
                )}
            </div>
        </div>
    );
};

const Tile = ({ label, value, valueClass = 'text-zinc-100' }) => (
    <div className="rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2.5">
        <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-medium">{label}</p>
        <p className={`text-base font-mono font-semibold ${valueClass}`}>{value}</p>
    </div>
);

const Row = ({ t }) => {
    const amt = Number(t.amount) || 0;
    const positive = amt > 0;
    const Icon = t.bucket === 'in' ? ArrowUpRight :
        t.bucket === 'out' ? ArrowDownRight :
            t.bucket === 'casino' ? (['slot_win', 'jackpot_grand', 'jackpot_super', 'lucky_pack_payout'].includes(t.type) ? Trophy : Dice5) :
                t.bucket === 'sports' ? Trophy : Coins;
    return (
        <div className="px-4 py-2.5 flex items-center gap-3" data-testid={`txn-row-${t.id}`}>
            <Icon className={`w-4 h-4 flex-shrink-0 ${positive ? 'text-emerald-300' : 'text-zinc-400'}`} />
            <div className="flex-1 min-w-0">
                <p className="text-sm text-zinc-100 font-medium truncate">
                    <PlayerName id={t.player_id || t.user_id} className="text-zinc-100 font-medium">
                        {t.player_name || '—'}
                    </PlayerName>
                    <span className="text-zinc-500 font-normal"> · {labelFor(t.type)}</span>
                </p>
                <p className="text-xs text-zinc-500 truncate">
                    {t.player_phone && <span className="font-mono">{t.player_phone}</span>}
                    {t.cashier_name && <> · by {t.cashier_name}</>}
                    {t.payout_method && <> · {t.payout_method}</>}
                    {(t.description || t.note) && <> · {t.description || t.note}</>}
                </p>
                <p className="text-[10px] text-zinc-600">{fmtTime(t.created_at)}</p>
            </div>
            <div className="text-right flex-shrink-0">
                <p className={`text-sm font-mono font-semibold ${positive ? 'text-emerald-300' : amt < 0 ? 'text-rose-300' : 'text-zinc-300'}`}>
                    {positive ? '+' : ''}{fmt(amt)}
                </p>
                <p className="text-[10px] font-mono text-zinc-500">{fmt(t.balance_after)}</p>
                {t.status && t.status !== 'paid' && (
                    <span className="inline-block mt-0.5 text-[9px] uppercase tracking-widest text-zinc-400 bg-zinc-800 border border-zinc-700 px-1.5 py-0.5 rounded">{t.status}</span>
                )}
            </div>
        </div>
    );
};

export default TransactionsTab;
